// WhatsApp-style message rows (docs/VISUAL-DESIGN.md §4, user decision 2026-10-13): the pure
// decisions behind one row of the timeline (and of the thread pane, which uses the same rows).
//
// A RUN is the existing group (`MessageListDerivation.rows` → `isGroupStart`). Others' messages sit
// on the leading side, own messages are mirrored to the trailing side; the avatar is drawn on the
// run's first message only; the author's name is the first line INSIDE the first bubble of a run
// (others only); every bubble carries its own short time at the trailing bottom, and hovering that
// time shows the full timestamp (weekday, date, time, plus the workspace's clock when the reader
// is on another one — the same annotation `MessageTimestamp` already decides).
//
// No DOM here: `ui/conversation/MessageRow.tsx` only draws what these say.
import { AvatarIdentity } from './avatarIdentity';
import { MessageTimestamp, type MessageTimestampOptions } from './messageTimestamp';
import { civil, dateFormatter, localTimeZone } from './time';
import { locale as uiLocale, tf } from '../i18n/i18n';

export type MessageSide = 'leading' | 'trailing';

export interface MessageRowInput {
  /** The signed-in user wrote it. */
  isOwn: boolean;
  /** First message of its run (`MessageListRow.isGroupStart`). */
  isGroupStart: boolean;
  /** The post has text to put in a bubble (`message.trim() !== ''`). */
  hasText: boolean;
}

export interface MessageRowPresentation {
  /** Others → leading, own → trailing. */
  side: MessageSide;
  /** Avatar in the side's 44 gutter: the run's first message only (own included). */
  showsAvatar: boolean;
  /**
   * The author's name: the first line inside the bubble — or, for a message without text, a small
   * label above its content. Others' first message of a run only; never on own messages.
   */
  showsName: boolean;
  /** Text gets a bubble; a message of only files/cards/embeds gets none (no invented bubble). */
  hasBubble: boolean;
  /** The 5 pt caret corner (pointing at the avatar): the run's first BUBBLE only. */
  caret: boolean;
}

function presentation(input: MessageRowInput): MessageRowPresentation {
  const { isOwn, isGroupStart, hasText } = input;
  return {
    side: isOwn ? 'trailing' : 'leading',
    showsAvatar: isGroupStart,
    showsName: isGroupStart && !isOwn,
    hasBubble: hasText,
    caret: isGroupStart && hasText,
  };
}

/** The time inside every bubble: the reader's short clock only (12/24 h per the account), no date, no home zone. */
function shortTime(epochMillis: number, options: MessageTimestampOptions = {}): string {
  return MessageTimestamp.parts(epochMillis, options).local;
}

export interface FullTimestampOptions extends MessageTimestampOptions {
  /** BCP-47 tag for weekday/month names; defaults to the UI language's. */
  locale?: string;
}

function capitalized(s: string): string {
  return s ? s.charAt(0).toLocaleUpperCase() + s.slice(1) : s;
}

/**
 * The hover tooltip on a message's time: "Martes, 13 de octubre de 2026 · 14:05", and on a reader
 * whose clock differs from the workspace's, "… · 04:22 (20:22 MX)". Built from the civil reading
 * (like the day separator) so the order is the apps' whatever Intl's locale data does.
 */
function fullTimestamp(epochMillis: number, options: FullTimestampOptions = {}): string {
  const zone = options.deviceZone ?? localTimeZone();
  const locale = options.locale ?? uiLocale();
  const c = civil(epochMillis, zone);
  const date = new Date(epochMillis);
  const weekday = capitalized(dateFormatter(locale, { weekday: 'long' }, zone).format(date));
  const month = dateFormatter(locale, { month: 'long' }, zone).format(date);
  const p = MessageTimestamp.parts(epochMillis, { deviceZone: zone, homeZone: options.homeZone });
  const time = p.home == null ? p.local : tf('web.msg.timestamp.home', p.local, p.home);
  return tf('web.msg.timestamp.full', weekday, c.day, month, c.year, time);
}

/**
 * What the author's name colour (and their avatar disc) is keyed on: the account id for a person;
 * for an integration its own picture, else its declared name — the id underneath is the hook's
 * OWNER, so keying on it would paint two different integrations of one owner the same.
 */
function identityKey(userId: string, webhook: { iconUrl: string; username: string } | null): string {
  if (webhook) return webhook.iconUrl || webhook.username || userId;
  return userId;
}

/** The author's AvatarIdentity colour (the same palette entry as their avatar disc). */
function nameColor(userId: string, webhook: { iconUrl: string; username: string } | null): string {
  return AvatarIdentity.color(identityKey(userId, webhook));
}

export const MessageRowLayout = {
  presentation,
  shortTime,
  fullTimestamp,
  identityKey,
  nameColor,
} as const;
